import { ChangeDetectorRef, Component, ElementRef, Input, ViewChild, SimpleChanges, OnInit, OnChanges } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { LoaderService } from 'app/modules/shared/components/loader/loader.service';
import { EntityService } from 'app/modules/shared/services/entity.service';
import { PermissionAccessService } from 'app/modules/shared/services/permission-access.service';
import * as _ from 'lodash';
import { ConfirmationService, MenuItem, MessageService } from 'primeng/api';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { TabViewModule } from 'primeng/tabview';
import { AppIcons } from '../../../../shared/app.icons';
import { AppSettings } from '../../../../shared/app.settings';
import { MITableComponent } from '../../../../shared/components/mi-table/mi-table.component';
import type { Country } from '../../../../shared/models/country';
import type { EntityCountsResponse, EntityList } from '../../../../shared/models/entity.models';
import type { Language } from '../../../../shared/models/language';
import type { ListResponse } from '../../../../shared/models/listResponse.models';
import { CommonBindingDataService } from '../../../../shared/services/common-binding-data.service';
import { ConfigService } from '../../../../shared/services/config.service';
import { MetaDataService } from 'app/modules/shared/services/meta-data.service';
import { RestApiService } from 'app/modules/shared/services/rest-api.service';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import type { AttributeData } from '../../../../shared/models/attribute.models';

interface ColumnDefinition {
  attributeCode: string;
  attributeIndex: number;
  field: string;
  header: string;
  [key: string]: unknown;
}

interface FilterField {
  attributeId: number;
  attributeCode: string;
  attributeIndex: number;
  presetValues: Array<{
    labelKey: string;
    labelValue: string;
  }>;
  [key: string]: unknown;
}

@Component({
  selector: 'app-orders',
  templateUrl: './orders.component.html',
  styleUrl: './orders.component.scss',
  standalone: true,
  imports: [
    TabViewModule,
    TranslateModule,
    MITableComponent,
    BreadcrumbModule,
    ButtonModule,
    DialogModule
  ]
})
export class OrdersComponent implements OnInit, OnChanges {
  @ViewChild('uploader', { static: true }) uploader: ElementRef<HTMLElement>;
  @ViewChild(MITableComponent) tableRef: MITableComponent;
  @Input() isActive: boolean = false;
  @Input() isSiteReady: boolean = false;
  @Input() siteEntityId: string;
  @Input() isChangeReasonNotRequired: boolean = false;
  @Input() isEditNotAllowed: boolean = false;
  @Input() isEditNotAllowedSpecificStatus: boolean = false;

  miIcons = AppIcons;
  activeIndex: number = 0;
  isLoading = true;
  filterAvailableColumns: string[] = [];
  cols: ColumnDefinition[] = [];
  moduleName = AppSettings.ENTITY_CODE.ORDER;
  filterList: FilterField[] = [];
  btnLabel: string;
  items: MenuItem[] | undefined;
  isShowMenu: boolean = true;
  attributeLabels: Record<string, string> = {};
  filterAttributeLabels: Record<string, string> = {};
  listColumns: ColumnDefinition[] = [];
  allColumns: ColumnDefinition[] = [];
  pagination: { limit: number; offset: number; searchStr: string; defaultSortColumn?: string; defaultSortType?: string } = {
    limit: AppSettings.PAGINATION_ROWS_PER_PAGE_LIMIT,
    offset: 0,
    searchStr: ""
  };
  totalRecords: number = 0;
  tableData: Record<string, unknown>[] = [];
  buttons: Array<{ label: string; icon: string; command: () => void }> = [];
  country: Country;
  language: Language;
  entityCount: EntityCountsResponse;
  itemsPerPage: number;
  entityData: EntityList = {
    limit: AppSettings.PAGINATION_ROWS_PER_PAGE_LIMIT,
    offset: 0,
    searchStr: "",
    filters: [],
    countryCode: '',
    deleted: AppSettings.DELETED_TYPE.ONLY_NON_DELETED,
    forTenantCode: "",
    actionStatus: ''
  };
  routePath: MenuItem[] = [];
  deleteSiteMsg: string;
  deleteSiteHeaderMsg: string;
  showMenu: boolean = false;
  moduleHeaderSecondaryBtnMenuItems: MenuItem[] = [];
  filterFieldsArray: FilterField[] = [];
  isReady: boolean = false;
  attributeData: AttributeData;
  data: unknown;
  rowItems: MenuItem[] | undefined;
  bulkSelectionsItems: MenuItem[] | undefined;
  currentRow: Record<string, unknown>;
  visiblePrintLabel: boolean = false;
  showBulkUpdateDialog: boolean = false;
  visible: boolean = false;
  bulkUpdateHeader: string;
  reviewHeader: string;
  btnUpdate: { label: string; icon: string; command: () => void };
  entityCreatedAtAttributeId: string;
  regionGeofences: Array<{ id: string; name: string }> = [];
  zoneIds: string[] = [];
  isEntity = false;
  showAddDialog: boolean = false;
  orderId: string;
  protocolEntityId: string;
  orderStatusAttributeId: string;
  trialId: string;
  protocolIdAttributeId: number;
  protocolDefaultSortColumnAttributeId: string;

  constructor(
    private entityService: EntityService,
    private cs: CommonBindingDataService,
    private confirmationService: ConfirmationService,
    private router: Router,
    private messageService: MessageService,
    private actRoute: ActivatedRoute,
    private metaDataService: MetaDataService,
    private restApiService: RestApiService,
    private configService: ConfigService,
    private permissionAccessService: PermissionAccessService,
    private cd: ChangeDetectorRef,
    private loaderService: LoaderService,
    private route: ActivatedRoute
  ) { }

  ngOnInit() {
    this.route.queryParams.subscribe(param => {
      this.trialId = param?.id
    });

    this.initializeLabels();
    this.initializeModuleHeaderSecondaryBtnMenuItems();
    this.initializeOtherVariablesAndAPI();
    this.getMetaData();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.isActive && changes.isActive.currentValue === true && !this.data) {
      this.initializeLabels();
      this.initializeModuleHeaderSecondaryBtnMenuItems();
      this.initializeOtherVariablesAndAPI();
      this.getMetaData();
    }
  }

  getMetaData() {
    const payload = this.getMultiApiPayload();
    this.metaDataService.metaDataMultipleApi(payload).subscribe((res) => {
      const advancedFilter = JSON.parse(res.advancedFilter.responseBody);
      if (advancedFilter) {
        this.setOrderFilterView(advancedFilter);
      }

      const sort = JSON.parse(res.sort.responseBody);
      const entityCreatedAt = sort.tabs[0].groups[0].fields.find(field => field.attributeCode === AppSettings.ENTITY_CREATED_AT);
      this.entityCreatedAtAttributeId = entityCreatedAt.attributeId;
      if (this.entityCreatedAtAttributeId) {
        // this.entityData.defaultSortColumn = this.entityCreatedAtAttributeId;
        this.entityData.defaultSortType = AppSettings.DESC_SORT_ORDER;
      }

      const defaultTableView = JSON.parse(res.defaultTableView.responseBody);
      if (defaultTableView) {
        this.setOrderTableView(defaultTableView);
      }
      this.cs.applyFilterToColumn(defaultTableView, sort);
    });
  }

  setOrderFilterView(filterResponse) {
    this.filterAttributeLabels = this.cs.getAttributeLabels(filterResponse) as unknown as Record<string, string>;
    this.filterList = this.cs.getColumns(filterResponse);
    this.protocolIdAttributeId = this.filterList.find(field => field.attributeCode === 'protocol_id')?.attributeId as number;
    this.filterList = _.sortBy(this.filterList, 'attributeIndex');
    this.filterList = this.filterList.filter(field => field.attributeCode === 'order_status');

    this.entityService.getAttributeDefinition(AppSettings.ENTITY_CODE.PROTOCOL, AppSettings.VIEW_CODE.DEFAULT_SORT_VIEW)
      .subscribe(sortColumnResponse => {
        if (sortColumnResponse) {
          this.protocolDefaultSortColumnAttributeId = this.cs.getColumns(sortColumnResponse)?.find(element => element.attributeCode === 'protocol_name')?.attributeId;
        }
      });

    const protocolIdField = this.filterList.find(field => field.attributeCode === 'protocol_id');
    if (protocolIdField) {
      protocolIdField.defaultSortColumn = this.protocolDefaultSortColumnAttributeId;
    }


    if (this.protocolIdAttributeId) {
      this.entityData.filters = [{
        attributeId: this.protocolIdAttributeId,
        attributeValue: this.trialId
      }]
    }

  }

  private getMultiApiPayload() {
    const view = this.configService.isSiteUser() ? AppSettings.VIEW_CODE.ORDER_ON_SITE_ADMIN_PROTOCOL_OVERVIEW : AppSettings.VIEW_CODE.ORDER_ON_SUPER_ADMIN_PROTOCOL_OVERVIEW;
    return [
      {
        url: `${AppSettings.BASE_URL}${this.configService.getLoggedInTenantCode()}/api/v1/entity-conf/${AppSettings.ENTITY_CODE.ORDER}/attributes?forTenantCode=${this.configService.getForTenantCode()}&viewCode=${AppSettings.VIEW_CODE.DEFAULT_SORT_VIEW}`,
        inputBody: '',
        method: 'GET',
        outputKey: AppSettings.META_OUTPUT_KEY.SORT,
        headers: this.restApiService.getHeaderForMultipleAPI()
      },
      {
        url: `${AppSettings.BASE_URL}${this.configService.getLoggedInTenantCode()}/api/v1/entity-conf/${AppSettings.ENTITY_CODE.ORDER}/attributes?forTenantCode=${this.configService.getForTenantCode()}&viewCode=${view}`,
        inputBody: '',
        method: 'GET',
        outputKey: AppSettings.META_OUTPUT_KEY.DEFAULT_TABLE_VIEW,
        headers: this.restApiService.getHeaderForMultipleAPI()
      },
      {
        url: `${AppSettings.BASE_URL}${this.configService.getLoggedInTenantCode()}/api/v1/entity-conf/${AppSettings.ENTITY_CODE.ORDER}/attributes?forTenantCode=${this.configService.getForTenantCode()}&viewCode=${AppSettings.VIEW_CODE.ADVANCED_FILTER_VIEW}`,
        inputBody: '',
        method: 'GET',
        outputKey: AppSettings.META_OUTPUT_KEY.ADVANCED_FILTER,
        headers: this.restApiService.getHeaderForMultipleAPI()
      }
    ]
  }

  onAdd() {
    this.showAddDialog = true;
  }



  onUpdateStatus(status, acceptLabelText) {
    const isActive = status === 'Active';

    this.confirmationService.confirm({
      header: this.cs.getLabel(isActive ? 'protocol_order.lbl_activate_confirmation' : 'protocol_order.lbl_deactivate_confirmation'),
      message: this.cs.getLabel(isActive ? 'protocol_order.msg_activate_confirmation' : 'protocol_order.msg_deactivate_confirmation'),
      acceptIcon: 'none',
      rejectIcon: 'none',
      rejectButtonStyleClass: 'bg-white text-color cancel',
      acceptButtonStyleClass: 'bg-red-500 delete',
      acceptLabel: acceptLabelText,
      rejectLabel: this.cs.getLabel('lbl_cancel'),
      accept: () => {
        const orderData = {
          forTenantCode: this.configService.getForTenantCode(),
          entityCode: this.moduleName,
          countryCode: this.country[0].countryCode,
          languageCode: this.language?.[0]?.langCode || 'en',
          data: [{
            attributeId: this.orderStatusAttributeId,
            attributeValue: status
          }],
          relationshipData: [],
          viewCode: AppSettings.VIEW_CODE.STATUS_UPDATE_VIEW,
        };

        this.entityService.saveAttributeData(orderData.entityCode, this.currentRow.id as string, orderData).subscribe({
          next: () => {
            this.messageService.add({
              severity: 'success',
              summary: 'Success',
              detail: this.cs.getLabel('protocol_order.lbl_status_updated_successfully'),
            });
            setTimeout(() => {
              this.searchEntity();
            }, 300);
          },
          error: () => {
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: this.cs.getLabel('order.lbl_failed_status')
            });
          }
        });
      },
      reject: () => {
      }
    });
  }

  private initializeLabels() {
    this.entityData.forTenantCode = this.configService.getForTenantCode();
    this.routePath = [{ label: "Orders", routerLink: 'app/orders' }];
  }

  private initializeOtherVariablesAndAPI() {
    this.btnLabel = this.cs.getLabel('Add Order');
    this.country = JSON.parse(localStorage.getItem(AppSettings.COUNTRY));
    this.language = JSON.parse(localStorage.getItem(AppSettings.LANGUAGE));
    this.deleteSiteMsg = this.cs.getLabel('order.message.confirm_delete_msg');
    this.deleteSiteHeaderMsg = this.cs.getLabel('order.message.confirm_delete_header');
  }

  setOrderTableView(res) {
    this.isLoading = false;
    this.data = res;
    this.attributeLabels = this.cs.getAttributeLabels(this.data) as unknown as Record<string, string>;
    this.allColumns = this.cs.getColumns(this.data);
    this.orderStatusAttributeId = this.allColumns.find(column => column.attributeCode === 'order_status')?.attributeId as string;
    this.searchEntity();
    this.allColumns?.forEach((key, index) => {
      this.allColumns[index] = key;
      this.allColumns[index].field = key.attributeCode;
      this.allColumns[index].header = this.cs.getLabelValue(
        `order` + `.fields.${key.attributeCode}.label`,
        this.attributeLabels,
        key.attributeCode
      );
      if (key.attributeCode === 'site_id') {
        this.allColumns[index].header = this.cs.getLabel('order.linked_site');
      }
      if (key.attributeCode === 'expected_delivery_date') {
        this.allColumns[index].header = this.cs.getLabel('order.lbl_delivery_date');
      }
    });
    this.filterAvailableColumns = _.clone(this.allColumns);
  }

  searchEntity() {
    try {
      const view = this.configService.isSiteUser() ? AppSettings.VIEW_CODE.ORDER_ON_SITE_ADMIN_PROTOCOL_OVERVIEW : AppSettings.VIEW_CODE.ORDER_ON_SUPER_ADMIN_PROTOCOL_OVERVIEW;

      this.loaderService.show();
      this.entityData.countryCode = AppSettings.HEADER_COUNTRY_CODE;
      this.entityData.tableViewCode = view;
      this.tableData = [];

      this.entityService.searchEntity(AppSettings.ENTITY_CODE.ORDER, this.entityData, false)
        .subscribe({
          next: (res: ListResponse) => {
            this.isSiteReady = true;
            if (!res?.data?.length) {
              this.tableData = [];
              this.totalRecords = 0;
              return;
            }
            this.listColumns = (res.data[0]?.values as unknown as ColumnDefinition[]) || [];
            this.pagination = res.pagination;
            this.totalRecords = res.count || 0;

            this.tableData = this.applySiteOrderStatusLabels(res.data.map((data: any) => {
              const flatRelatedValues = (data.relatedData || []).reduce((acc: any, item: any) => {
                if (item.entityCode !== 'order') {
                  return {
                    ...acc,
                    ...(item.values || {})
                  };
                }
                return acc;
              }, {});

              const orderData = (data.relatedData || []).filter(item => item.entityCode === 'order')
                .map(item => ({
                  id: item.id,
                  ...(item.values || {})
                }));
              return {
                id: data.id,
                actionStatus: data.actionStatus,
                ...(data.values || {}),
                ...flatRelatedValues,
                order: orderData
              };
            }));
          },
          error: () => {
            this.tableData = [];
            this.totalRecords = 0;
          },
          complete: () => {
            this.loaderService.hide();
          }
        });
    } catch (error) {
      this.loaderService.hide();
      this.tableData = [];
      this.totalRecords = 0;
    }
  }

  private applySiteOrderStatusLabels(data: Record<string, unknown>[]): Record<string, unknown>[] {
    if (!this.configService.isSiteUser()) {
      return data;
    }

    const siteStatusMap: Record<string, string> = {
      'New Order': 'Order Submitted',
      'Order Received': 'Order Delivered',
    };

    return data.map(row => {
      const orderStatus = row?.order_status;
      if (!orderStatus || typeof orderStatus !== 'object') {
        return row;
      }

      const mappedOrderStatus = Object.entries(orderStatus as Record<string, string>).reduce((acc, [key, value]) => {
        acc[key] = siteStatusMap[value] ?? value;
        return acc;
      }, {} as Record<string, string>);

      return { ...row, order_status: mappedOrderStatus };
    });
  }

  onPageChange(event) {
    this.entityData.offset = event?.first;
    this.entityData.limit = event?.rows;
    this.searchEntity();
  }

  itemPerPageChange(event) {
    this.itemsPerPage = event;
    this.entityData.limit = this.itemsPerPage;
    this.searchEntity();
  }

  onFilterValueChange(event) {
    const vData = event;
    this.entityData.filters = [];
    this.entityData.filters = [{
      attributeId: this.protocolIdAttributeId,
      attributeValue: this.trialId
    }]
    for (const [key, value] of Object.entries(vData)) {
      const attributeValue = value;
      if (attributeValue) {
        this.entityData.filters.push({
          attributeCode: key,
          attributeValue
        });
      }
    }
    this.entityData.filters = _.filter(_.uniq(this.entityData.filters, (item) => {
      if (item.attributeValue.length > 0) {
        return item.attributeCode;
      }
    }), (element) => {
      if (element.attributeValue.length > 0) {
        return true;
      }
      return false;
    });
    this.entityData.offset = event.first;
    this.entityData.filters = this.cs.mapAttributeIds(this.entityData.filters, this.filterList, 'filter');
    this.searchEntity();
  }

  onSearchValueChanges(event) {
    this.entityData.searchStr = event;
    this.searchEntity();
  }

  setCurrentRowData(event) {
    this.currentRow = event;

    if (event.deleted) {
      this.rowItems.forEach(item => {
        if (!item.separator) {
          item.disabled = true;
        }
      });
      return;
    }

    this.rowItems.forEach(item => {
      switch (item.value) {
        case 'edit':
          item.disabled = this.isEditNotAllowed;
          break;
        case 'active':
          item.disabled = event.order_status?.Active === 'Active' || this.isEditNotAllowed;
          break;
        case 'deactive':
          item.disabled = event.order_status?.Inactive === 'Inactive' || this.isEditNotAllowed;
          break;
      }
    });
  }

  update() {
    this.onUpdateOrder(this.currentRow.id);
  }

  onUpdateOrder(orderId) {
    this.orderId = orderId;
    this.showAddDialog = true;
  }

  openAddDialog() {
    if (this.isSiteReady) {
      this.showAddDialog = true;
    }
  }

  onDialogVisibleChange() {
    // Handle dialog visibility change
  }

  onDialogClose() {
    this.showAddDialog = false;
    this.orderId = null;
    setTimeout(() => {
      this.searchEntity();
    }, 100);
  }

  onSaveSuccess() {
    this.showAddDialog = false;
    this.orderId = null;
    this.searchEntity();
  }

  private initializeModuleHeaderSecondaryBtnMenuItems() {
    // Initialize secondary button menu items if needed
  }
}


